import { glass } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";
import Link from "next/link";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import * as awarenessProtocol from "y-protocols/awareness";
import { useAwarenessEntries } from "../../../libEdit/yfs/useAwarenessEntries";
import { YFSClient } from "../../../libEdit/yfs/YFSClient";
import UserAvatar from "../common/UserAvatar";

export default function Presence({
  yfsClient,
  awareness,
}: {
  yfsClient: YFSClient;
  awareness: awarenessProtocol.Awareness;
}) {
  const [tooltip, setTooltip] = useState<{ username: string; path: string } | null>(null);
  const awarenessEntries = useAwarenessEntries(awareness);

  return (
    <div key="presence-container" className="relative">
      <div className="max-w-[150px] rounded-[20px] overflow-x-scroll no-scrollbar">
        <div className="flex justify-end -space-x-1.5 px-0.5 min-w-fit">
          {awarenessEntries.map(([clientID, awarenessEntry], index) => (
            <PresenceItem
              key={clientID}
              yfsClient={yfsClient}
              clientIDCurrent={awareness.clientID}
              clientID={clientID}
              awarenessEntry={awarenessEntry}
              index={index}
              setTooltip={setTooltip}
            />
          ))}
        </div>
      </div>

      {tooltip && (
        <div className="z-[999] bg-gray-100/98 px-2 py-1 border border-gray-400 rounded absolute right-0 -bottom-16 flex flex-col items-end text-left font-medium text-base text-gray-700">
          <p className="w-full whitespace-nowrap font-semibold text-sm">{tooltip.username}</p>
          <p className="w-full whitespace-nowrap font-mono font-bold text-xs">{tooltip.path}</p>
        </div>
      )}
    </div>
  );
}

function PresenceItem({
  yfsClient,
  clientIDCurrent,
  clientID,
  awarenessEntry,
  index,
  setTooltip,
}: {
  yfsClient: YFSClient;
  clientIDCurrent: number;
  clientID: number;
  awarenessEntry: any;
  index: number;
  setTooltip: Dispatch<SetStateAction<{ username: string; path: string } | null>>;
}) {
  const userId = awarenessEntry?.["user"]?.["userId"];
  const name = awarenessEntry?.["user"]?.["name"] ?? "unknown";
  const username = `@${name}`;

  /* --- AVATAR FALLBACK ---------------------------------- */
  const awarenessAvatar = useMemo(() => {
    const avatar = createAvatar(glass, { seed: `${clientID}` });
    const svgString = avatar.toString().replace(/[^\x00-\xFF]/g, "");
    const base64encoded = btoa(svgString);
    return `data:image/svg+xml;base64,${base64encoded}`;
  }, [clientID]);

  return (
    <Link
      key={clientID}
      href={yfsClient.getUrlFromPath(awarenessEntry["path"] ?? "/")}
      className="opacity-90 hover:opacity-100 relative size-10 rounded-full"
      style={{ zIndex: index }}
      onMouseEnter={() =>
        setTooltip({
          username: username + (clientID === clientIDCurrent ? " (you)" : ""),
          path: awarenessEntry["path"] ?? "/",
        })
      }
      onMouseLeave={() => setTooltip(null)}
    >
      <div
        className="absolute inset-0 size-full rounded-full opacity-70"
        style={{ backgroundColor: awarenessEntry?.["user"]?.["color"] ?? "transparent" }}
      />
      <img
        alt="Collaborator avatar"
        src={awarenessAvatar}
        className="absolute inset-0 size-full rounded-full z-10 flex-none"
      />
      {userId && (
        <UserAvatar
          userId={userId}
          className="absolute inset-0 size-full rounded-full z-10 flex-none border border-gray-400"
          showPlaceholder={false}
        />
      )}
    </Link>
  );
}
